import React, { Suspense } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import "./pages/shared/tokens.css";
import AnalyticsPageTracker from "./pages/shared/AnalyticsPageTracker";
import MarketingRoutePreloader from "./pages/shared/MarketingRoutePreloader";
import RouteLoadingFallback from "./pages/shared/RouteLoadingFallback";
import { marketingRouteLoaders } from "./pages/shared/marketingRouteLoaders";

const HomePage = React.lazy(marketingRouteLoaders.home);
const ProductsPage = React.lazy(marketingRouteLoaders.products);
const SatelliteProductsPage = React.lazy(marketingRouteLoaders.satelliteProducts);
const AboutPage = React.lazy(marketingRouteLoaders.about);
const MarketsPage = React.lazy(marketingRouteLoaders.markets);
const MarketDetailPage = React.lazy(marketingRouteLoaders.marketDetail);
const TrVoicePage = React.lazy(marketingRouteLoaders.trVoice);
const TrFlowPage = React.lazy(marketingRouteLoaders.trFlow);
const TrIotPage = React.lazy(marketingRouteLoaders.trIot);
const TrVisionPage = React.lazy(marketingRouteLoaders.trVision);
// Keep the legacy chat/product application out of every marketing route chunk.
const LegacyApp = React.lazy(() => import("./App"));

// Marketing routes that should open at the top when navigated to. The legacy
// app (other routes) manages its own scroll.
const MARKETING_PATHS = [
  "/",
  "/products",
  "/satellite-products",
  "/about",
  "/markets",
  "/markets/cities",
  "/markets/buildings",
  "/markets/transport",
  "/markets/retail",
  "/markets/infrastructure",
  "/markets/government",
  "/product/iot",
  "/product/trvision",
  "/product/trvoice",
  "/product/trflow",
];

/**
 * React Router does not reset scroll on client-side navigation, so following a
 * link from a scrolled page leaves the next page scrolled to the same offset.
 * Reset to the top on forward navigation, while preserving:
 *  - back/forward (POP): HomePage restores its own saved scroll position;
 *  - hash targets: ProductsPage scrolls to a specific card on return from a
 *    product detail page.
 */
const ScrollToTop: React.FC = () => {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();
  // useLayoutEffect resets the scroll before the browser paints, so the new
  // page never flashes at the previous scroll offset.
  React.useLayoutEffect(() => {
    if (!MARKETING_PATHS.includes(pathname)) return;
    if (navType === "POP") return;
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, [pathname, hash, navType]);
  return null;
};

const AppRoutes: React.FC = () => (
  <BrowserRouter>
    <ScrollToTop />
    <MarketingRoutePreloader />
    <Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
      {/* StrictMode is scoped to the new marketing pages so its dev-only
          double-invocation surfaces issues in this code without destabilizing
          the large legacy app mounted on the fallback route. */}
      <Route
        path="/"
        element={
          <React.StrictMode>
            <HomePage />
          </React.StrictMode>
        }
      />
      {/* caseSensitive so legacy uppercase URLs (e.g. /Products) fall through to
          LegacyApp instead of being intercepted by these marketing routes. */}
      <Route
        path="/products"
        caseSensitive
        element={
          <React.StrictMode>
            <ProductsPage />
          </React.StrictMode>
        }
      />
      <Route
        path="/satellite-products"
        caseSensitive
        element={
          <React.StrictMode>
            <SatelliteProductsPage />
          </React.StrictMode>
        }
      />
      <Route
        path="/about"
        caseSensitive
        element={
          <React.StrictMode>
            <AboutPage />
          </React.StrictMode>
        }
      />
      <Route
        path="/markets"
        caseSensitive
        element={
          <React.StrictMode>
            <MarketsPage />
          </React.StrictMode>
        }
      />
      <Route
        path="/markets/:marketId"
        caseSensitive
        element={<React.StrictMode><MarketDetailPage /></React.StrictMode>}
      />
      <Route
        path="/product/trvoice"
        caseSensitive
        element={
          <React.StrictMode>
            <TrVoicePage />
          </React.StrictMode>
        }
      />
      <Route
        path="/product/trvision"
        caseSensitive
        element={
          <React.StrictMode>
            <TrVisionPage />
          </React.StrictMode>
        }
      />
      <Route
        path="/product/iot"
        caseSensitive
        element={
          <React.StrictMode>
            <TrIotPage />
          </React.StrictMode>
        }
      />
      <Route
        path="/product/trflow"
        caseSensitive
        element={
          <React.StrictMode>
            <TrFlowPage />
          </React.StrictMode>
        }
      />
      {/*
        Everything else (the chat assistant, /product/:name detail pages,
        /privacy-policy, and the legacy drawer routes) is handled by the
        existing app, which routes internally off window.location.
      */}
      <Route path="*" element={<LegacyApp />} />
      </Routes>
    </Suspense>
    <AnalyticsPageTracker />
  </BrowserRouter>
);

export default AppRoutes;
